<template>
  <div class="ui-json-tree">
    <Tree
      v-model:expanded-paths="expandedPaths"
      :data
      :size
      :meta-fields="[VALUE_FIELD]"
      root="root"
      placeholder="Search value..."
      :expanded="[]"
      search-in-meta
      @click:node="handleClickNode"
    >
      <template #name="{ isRoot }">
        <slot v-if="isRoot" />
      </template>
      <template #info>
        <span
          class="ui-json-tree__info-link"
          @click="handleDownload"
        >Download</span> to search locally.
      </template>
      <template #meta="slotProps">
        <template v-if="slotProps.isRoot">
          <span class="ui-json-tree__copy">
            <span
              class="ui-json-tree__copy-text"
              :class="copiedTextClasses"
            >Copied</span>
            <component
              :is="copyIcon"
              v-tooltip.top="copyTooltip"
              class="ui-json-tree__copy-icon"
              aria-hidden="true"
              @click="handleCopy"
            />
          </span>
          <ArrowDownTrayIcon
            v-tooltip.top="'Download JSON'"
            class="ui-json-tree__download"
            aria-hidden="true"
            @click="handleDownload"
          />
        </template>
        <TreeJsonValue
          v-else
          :value="slotProps[VALUE_FIELD]"
          :search="slotProps.search"
        />
      </template>
    </Tree>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { InputSize } from '@types'

import Tree from './Tree.vue'
import TreeJsonValue from './components/TreeJsonValue.vue'
import {
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/vue/24/outline'
import { downloadAsFile } from '@/helpers/common'

const VALUE_FIELD = '#__value'

type Props = {
  json: any
  size?: InputSize
  downloadName?: string
}

type Emits = {
  'click:node': [path: string[], value: any]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const isCopied = ref(false)
const copyTimeout = ref<NodeJS.Timeout>()
const expandedPaths = ref<Set<string>>(new Set())

const data = computed(() => {
  if (!props.json) return
  const root = convertJsonToTree(props.json)
  return { root }
})

const copyIcon = computed(() =>
  isCopied.value ? ClipboardDocumentCheckIcon : ClipboardDocumentIcon,
)

const copyTooltip = computed(() =>
  isCopied.value ? undefined : 'Copy to clipboard',
)

const copiedTextClasses = computed(() => ({
  'ui-json-tree__copy-text--visible': isCopied.value,
}))

const handleDownload = () => {
  const fileData = JSON.stringify(props.json)
  downloadAsFile(fileData, props.downloadName || 'json', 'json')
}

const handleCopy = () => {
  const data = JSON.stringify(props.json)
  const blob = new Blob([data], { type: 'text/plain' })
  navigator.clipboard.write([
    new ClipboardItem({
      [blob.type]: blob,
    }),
  ])
  isCopied.value = true
  copyTimeout.value && clearTimeout(copyTimeout.value)
  copyTimeout.value = setTimeout(() => {
    isCopied.value = false
  }, 2000)
}

const convertJsonToTree = (data: any) => {
  const result: any = {}
  if (Array.isArray(data)) {
    result[VALUE_FIELD] = `[${data.length}]`
    for (let i = 0; i < data.length; i++) {
      const nodes = convertJsonToTree(data[i])
      result[i] = {
        [VALUE_FIELD]: '...',
        ...nodes,
      }
    }
  } else if (typeof data === 'object' && data !== null) {
    for (const key in data) {
      const nodes = convertJsonToTree(data[key])
      result[key] = {
        [VALUE_FIELD]: '...',
        ...nodes,
      }
    }
  } else if (data === '') {
    result[VALUE_FIELD] = '\'\''
  } else {
    result[VALUE_FIELD] = `${data}`
  }
  return result
}

const handleClickNode = (path: string[], value: any) => {
  emit('click:node', path, value[VALUE_FIELD])
}
</script>

<style>
.ui-json-tree {
  @apply flex flex-auto;

  &__copy {
    @apply flex;
    @apply gap-1 ml-auto;

    &-icon {
      @apply w-4 h-4;
      @apply cursor-pointer;
      @apply text-gray-500 hover:text-indigo-500;
    }

    &-text {
      @apply text-sm sm:text-xs;
      @apply text-gray-500;
      @apply opacity-0;
      @apply transition-opacity duration-200;

      &--visible {
        @apply opacity-100;
      }
    }
  }

  &__download {
    @apply w-4 h-4;
    @apply ml-2;
    @apply cursor-pointer;
    @apply text-gray-500 hover:text-indigo-500;
  }

  .ui-tree__item-area--root .ui-tree__name {
    @apply truncate;
    @apply shrink flex-auto;
  }

  .ui-tree__item-area--root .ui-tree__value {
    @apply flex flex-auto items-center justify-end;
    @apply overflow-visible;
  }

  &__info-link {
    @apply text-yellow-600;
    @apply underline hover:no-underline;
    @apply cursor-pointer;
  }
}
</style>
